import { EventEmitter, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as mapboxgl from 'mapbox-gl';
import { finalize, take } from 'rxjs/operators';
import { Building } from 'src/app/geometrics-form/buildings/state/building.model';
import { BuildingService } from 'src/app/geometrics-form/buildings/state/building.service';
import { EdgeFormComponent } from 'src/app/geometrics-form/edges/edge-form/edge-form.component';
import { createEdge } from 'src/app/geometrics-form/edges/state/edge.model';
import { EdgeQuery } from 'src/app/geometrics-form/edges/state/edge.query';
import { EdgeService } from 'src/app/geometrics-form/edges/state/edge.service';
import { GeometricFormTypeEnum } from 'src/app/geometrics-form/state/geometric-form.type';
import { Vertex } from 'src/app/geometrics-form/vertexes/state/vertex.model';
import { VertexService } from 'src/app/geometrics-form/vertexes/state/vertex.service';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { LayerAction } from './layer-action';

@Injectable({
  providedIn: 'root'
})
export class FeatureSelectionService extends LayerAction {

  selecteds$ = new EventEmitter<Map<number, mapboxgl.MapboxGeoJSONFeature>>();

  private selectionMap = new Map<number, mapboxgl.MapboxGeoJSONFeature>();
  private mapbox: mapboxgl.Map;

  constructor(
    private buildingService: BuildingService,
    private edgeService: EdgeService,
    private vertixeService: VertexService,
    private windowService: WindowService,
    private edgeQuery: EdgeQuery
  ) {
    super();
  }

  listen(mapbox: mapboxgl.Map, layerName: string) {
    this.mapbox = mapbox;
    this.onClick(layerName);
  }

  private onClick(layerName: string) {
    this.mapbox.on('click', layerName, (e) => {

      if (!e.defaultPrevented && e.originalEvent.ctrlKey) {
        const feature = e.features[0];

        if (feature) {
          this.selection(feature);
        }
        e.preventDefault();
        e.originalEvent.preventDefault();
      }
    });
  }

  private selection(feature: mapboxgl.MapboxGeoJSONFeature) {
    const id = Number(feature.id);

    if (this.selectionMap.has(id)) {
      this.mapbox.setFeatureState(feature, { m_selected: false });
      this.selectionMap.delete(id);
      this.delete(id);
      return;
    }

    this.mapbox.setFeatureState(feature, { m_selected: true });

    this.add(feature);

    if (this.selectionMap.size === 2) {
      const features = Array.from(this.selectionMap.values());
      this.action(features[0], features[1]);
    }
  }

  private add(feature: mapboxgl.MapboxGeoJSONFeature) {
    this.selectionMap.set(Number(feature.id), feature);
    this.selecteds$.emit(this.selectionMap);
  }

  private delete(id: number) {
    this.selectionMap.delete(id);
    this.selecteds$.emit(this.selectionMap);
  }

  private action(f1: mapboxgl.MapboxGeoJSONFeature, f2: mapboxgl.MapboxGeoJSONFeature) {

    if (this.isVertex(f1) && this.isVertex(f2)) {
      const v1 = f1.properties as Vertex;
      const v2 = f2.properties as Vertex;

      if (!this.edgeQuery.exists(v1.id, v2.id)) {
        this.addEdge(v1, v2);
      }

    } else if (this.isVertex(f1) && this.isBuilding(f2)) {
      this.connectBuildingVertex(f2.properties as Building, f1.properties as Vertex);

    } else if (this.isBuilding(f1) && this.isVertex(f2)) {
      this.connectBuildingVertex(f1.properties as Building, f2.properties as Vertex);
    }
  }

  private addEdge(v1: Vertex, v2: Vertex) {

    if (this.edgeService.getEdgeClasse(v1, v2)) {
      this.edgeService.create(v1, v2).subscribe((edge) => {
        this.clear();
        this.vertixeService.getFull(v1.id).pipe(take(1)).subscribe();
        this.vertixeService.getFull(v2.id).pipe(take(1)).subscribe();
      });

    } else {
      const edge = createEdge({
        vertex_a: v1.id,
        vertex_b: v2.id,
        va_latitude: v1.latitude,
        va_longitude: v1.longitude,
        vb_latitude: v2.latitude,
        vb_longitude: v2.longitude
      });
      const titleConfig = $localize`NEW EDGE`;
      this.windowService.open({
        title: titleConfig,
        componentType: EdgeFormComponent,
        help: 'CREATE_EDGES',
        config: { data: edge }
      }).subscribe(() => this.clear());
    }

  }

  private connectBuildingVertex(building: Building, vertex: Vertex) {
    this.buildingService.connect(building, vertex)
      .pipe(
        finalize(() => this.clear())
      )
      .subscribe();
  }

  private clear() {
    this.selectionMap.forEach(feature => {
      this.mapbox.setFeatureState(feature, { m_selected: false });
      this.selectionMap.delete(feature.id as number);
    });
  }

  private isVertex(f: mapboxgl.MapboxGeoJSONFeature) {
    return f.layer.id === GeometricFormTypeEnum.VERTEX;
  }

  private isBuilding(f: mapboxgl.MapboxGeoJSONFeature) {
    return f.layer.id === GeometricFormTypeEnum.BUILDING;
  }
}
